import { Input, PolicyDispatchingPermission, assets } from '@pulseops/common'
import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import PDTable from './components/PDTable'
import PolicyDispatchingDialog from './components/PolicyDispatchingDialog'
import { ActivityIndicator, View, Text } from 'react-native'
import { ZIO } from '@mxt/zio'
import { pipe } from 'fp-ts/function'
import { borderedInputStyle } from './styles/policyDispatchingStyles'
import {
  PolicyDispatchingService,
  AckNotReturnSearchItem,
  AckNotReturnSearchResponse
} from './services/PolicyDispatchingService'
import { getACKStatus, getPermissions } from './utils/policyDispatchingUtils'

const headCell = [
  { id: 1, label: 'Số hợp đồng' },
  { id: 2, label: 'Số HSYCBH' },
  { id: 3, label: 'Ngày phát hành' },
  { id: 4, label: 'Tình trạng' },
  { id: 5, label: 'Định kỳ phí' },
  { id: 6, label: 'Chủ hợp đồng' },
  { id: 7, label: 'Ngày nhập kho' },
  { id: 8, label: 'Ngày giao HĐ' },
  { id: 9, label: 'VP giao HĐ' },
  { id: 10, label: 'Người nhận' },
  { id: 11, label: 'Số ngày trễ' }
]

const rowCell = [
  { id: 1, label: 'policyNumber' },
  { id: 2, label: 'requestPolicyNumber' },
  { id: 3, label: 'releasedDate' },
  { id: 4, label: 'status' },
  { id: 5, label: 'period' },
  { id: 6, label: 'contractOwner' },
  { id: 7, label: 'enterDate' },
  { id: 8, label: 'deliveryDate' },
  { id: 9, label: 'office' },
  { id: 10, label: 'receiver' },
  { id: 11, label: 'latedDate' }
]

type NotReturnLetterRow = {
  policyNumber: string
  requestPolicyNumber: string
  releasedDate: string
  status: string
  period: string
  contractOwner: string
  enterDate: string
  deliveryDate: string
  office: string
  receiver: string
  latedDate: string
}

type FormValues = {
  agentNumber: string
  branchName: string
}

const toRow = (item: AckNotReturnSearchItem): NotReturnLetterRow => ({
  policyNumber: item.contractNumber || '',
  requestPolicyNumber: item.referenceNumber || '',
  releasedDate: item.policyIssueDate || '',
  status: getACKStatus(item.statusCode),
  period: item.billingFrequency || '',
  contractOwner: item.ownerName || '',
  enterDate: item.stockInDate || '',
  deliveryDate: item.deliveryDate || '',
  office: item.office || '',
  receiver: item.receiver || '',
  latedDate: item.overdueDays !== null && item.overdueDays !== undefined ? String(item.overdueDays) : ''
})

const hasAckNotReturnBody = (
  response: AckNotReturnSearchResponse
): response is Extract<AckNotReturnSearchResponse, { body: unknown }> => 'body' in response

export default function PDNotReturnLetterScreen() {
  const { control, handleSubmit, getValues, setValue } = useForm<FormValues>({
    defaultValues: {
      agentNumber: '',
      branchName: ''
    }
  })
  const [rows, setRows] = React.useState<NotReturnLetterRow[]>([])
  const [pageInfo, setPageInfo] = React.useState<{
    page: number
    totalPages: number
    totalElements: number
    first: boolean
    last: boolean
  } | null>(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const [modalInfo, setModalInfo] = React.useState({ title: '', message: '' })
  const [openModal, setOpenModal] = React.useState(false)
  const permissions = getPermissions()
  const allowSearch = permissions.includes(PolicyDispatchingPermission.QuickSearchSearch)

  const search = (values: FormValues, nextPage: number) => {
    if (!values.agentNumber) {
      setModalInfo({ title: 'Tìm kiếm', message: 'Vui lòng nhập mã số đại lý trước khi tìm kiếm.' })
      setOpenModal(true)
      return
    }

    setIsLoading(true)
    pipe(
      PolicyDispatchingService.searchAckNotReturn({ agentNumber: values.agentNumber, page: nextPage }),
      ZIO.tap((response) =>
        ZIO.effectTotal(() => {
          if (!hasAckNotReturnBody(response)) {
            setRows([])
            setPageInfo(null)
            setIsLoading(false)
            return
          }

          setRows(response.body.items.map(toRow))
          setValue('branchName', response.body.agentName || '')
          setPageInfo(response.body.page)
          setIsLoading(false)
        })
      ),
      ZIO.catchAll((error) =>
        ZIO.effectTotal(() => {
          console.error('Unable to search ack not return letters', error)
          setRows([])
          setPageInfo(null)
          setIsLoading(false)
          setModalInfo({
            title: 'Tìm kiếm thất bại',
            message: 'Không thể tải thông tin thư xác nhận chưa hoàn trả. Vui lòng thử lại sau.'
          })
          setOpenModal(true)
        })
      ),
      ZIO.unsafeRun({})
    )
  }

  const handleSearch = handleSubmit((values) => search(values, 0))

  return (
    <View style={{ ...styles.container, position: 'relative' } as any}>
      <PolicyDispatchingDialog
        visible={openModal}
        title={modalInfo.title}
        message={modalInfo.message}
        onClose={() => setOpenModal(false)}
      />
      {isLoading && (
        <View style={styles.loadingOverlay}>
          <ActivityIndicator size="large" color="#ED1B2E" />
        </View>
      )}
      <Text style={styles.title}>Tìm nhanh</Text>
      <View style={styles.wrapper}>
        <Text style={styles.subTitle}>Thông tin thư xác nhận chưa hoàn trả</Text>
        <View
          style={
            {
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 24
            } as any
          }
        >
          <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 16, width: '50%' } as any}>
            <View style={{ width: '30%' } as any}>
              <Controller
                name="agentNumber"
                control={control}
                render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                  <Input
                    title={'Mã số đại lý'}
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value}
                    errorMessage={error?.message}
                    containerStyle={{ flex: 1 }}
                    inputStyle={borderedInputStyle}
                    filled
                    required
                  />
                )}
              />
            </View>
            <View style={{ width: '50%', marginTop: 22 } as any}>
              <Controller
                name="branchName"
                control={control}
                render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                  <Input
                    title={''}
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value}
                    errorMessage={error?.message}
                    containerStyle={{ flex: 1 }}
                    inputStyle={borderedInputStyle}
                    filled
                    required
                    disabled
                    alwayShowUnderline
                  />
                )}
              />
            </View>
            <button
              style={{
                marginTop: 24,
                minHeight: 40,
                ...(isLoading || !allowSearch ? styles.disabledButton : styles.submitButton)
              }}
              type="button"
              onClick={handleSearch}
              disabled={isLoading || !allowSearch}
            >
              <span style={styles.mr_5}>
                <assets.SearchWhite />
              </span>
              Tìm
            </button>
          </View>
          <Text style={{ fontSize: 12, color: '#6b7282' } as any}>Ngày: 13/04/1998</Text>
        </View>
        <PDTable
          headCell={headCell}
          rowCell={rowCell}
          data={rows}
          pagination={
            pageInfo
              ? {
                  page: pageInfo.page,
                  totalPages: pageInfo.totalPages,
                  totalElements: pageInfo.totalElements,
                  first: pageInfo.first,
                  last: pageInfo.last,
                  onPageChange: (nextPage) => search(getValues(), nextPage)
                }
              : undefined
          }
        />
      </View>
    </View>
  )
}

const styles: Record<string, any> = {
  container: {
    paddingTop: 0,
    paddingRight: 40,
    paddingBottom: 30,
    paddingLeft: 40,
    height: '100%',
    overflow: 'auto'
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    marginBottom: '20px'
  },
  wrapper: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '20px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
  },
  subTitle: {
    color: '#fff',
    fontSize: '18px',
    fontWeight: 'bold',
    paddingTop: 4,
    paddingRight: 12,
    paddingBottom: 4,
    paddingLeft: 12,
    borderRadius: 4,
    backgroundColor: '#e7000b',
    marginBottom: 16,
    width: 'fit-content'
  },
  submitButton: {
    backgroundColor: '#ED1B2E',
    color: '#fff',
    borderWidth: 0,
    paddingTop: 6,
    paddingRight: 16,
    paddingBottom: 6,
    paddingLeft: 16,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    fontWeight: '600'
  },
  disabledButton: {
    backgroundColor: '#e5e7eb',
    color: '#9ca3af',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#d1d5db',
    paddingTop: 6,
    paddingRight: 16,
    paddingBottom: 6,
    paddingLeft: 16,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    fontWeight: '600',
    cursor: 'not-allowed',
    opacity: 0.7
  },
  mr_5: {
    marginRight: '5px'
  },
  loadingOverlay: {
    position: 'fixed' as const,
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    zIndex: 2
  }
}
